import React, { FunctionComponent, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Button, Message, Grid } from '@afterpaytouch/core'
import { useConsumerSelector, useIdentityLicenseMutation, useIsPartialVerified, useAppRouter } from '../../../state'
import { getInitialFormData, getLicenceCardNumberValidation, getLicenceValidations, getSchema, isLicenceNumValid, setHiddenFieldsByCountry } from './utils'
import { Terms } from './Terms'
import { FormWrapper } from '..'
import FormBuilder from '@afterpaytouch/form'
import { DriversLicenceForm, LicenceNumOpts, SchemaUpdates } from './types'
import { isDifferentWithProfileDOB, isDifferentWithProfileNames, IsRetryPageContext } from '../VerifyIdentity/helper'
import { DocumentProps } from '../../../model'
import { Channel, IdentityDocumentVerificationStatus, IdentityLicenseRequest } from '@afterpaytouch/portal-api/identity/types'
import { Country, ErrorCode } from '@afterpaytouch/portal-api/types'
import { joinStrings } from '@afterpay/utils/string'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { TrackingEvent } from '../../../model/amplitude'
import { getIdentityVerifyErrorCode, IdentityErrorCode } from '@afterpaytouch/portal-api/identity'
import { compareObjects } from '../../../utils/comparison'
import { USER_VERIFICATION_ERRORS } from '../VerifyIdentity/constants'
import { useCountryStateIsShown } from '../../../state/identity/hooks'
import { HardIdProfileCard, ErrorMessage, HardIdHelpLink, HardIdHeader } from '../VerifyIdentity/components'
import type { FieldErrors } from '@afterpaytouch/form'
import { VerifyLaterModal } from '../VerifyLaterModal/VerifyLaterModal'
import { Route } from '../../../config/router'

const I18N_NAMESPACE = ['common', 'verifyIdentity']

export const DriversLicence: FunctionComponent<DocumentProps> = ({ onSuccess, onFailure }) => {
  const amplitude = useAmplitudeWithEnduringEventProperties()
  const [lock, setLock] = useState(false)
  const [error, setError] = useState<IdentityErrorCode | null>(null)
  const { t } = useTranslation(I18N_NAMESPACE)
  const consumer = useConsumerSelector()
  const { countryCode, dateOfBirth, givenNames, otherGivenNames, surname } = consumer
  const { state: initialState } = getInitialFormData(consumer)
  const lastSelectedState = useRef(initialState)
  const showCardNumber = countryCode === Country.AU
  const router = useAppRouter()
  const [showVerifyLaterModal, setShowVerifyLaterModal] = useState(false)
  const [schemaUpdates, setSchemaUpdates] = useState<SchemaUpdates>({
    licenceNumOpts: {} as LicenceNumOpts,
    cardNumOpts: {} as LicenceNumOpts,
    showProfileDobAlert: false,
    showProfileNameAlert: false,
    licenceMaxLength: 0,
  })
  const [submittedRequest, setSubmittedRequest] = useState(undefined)
  const [identifyVerifyLicence] = useIdentityLicenseMutation()
  const profileDOB = dateOfBirth ?? ''
  const countryStateIsShowing = useCountryStateIsShown()
  const isPartialVerified = useIsPartialVerified()
  const isRetryPage = useContext(IsRetryPageContext)
  const isProfileEditable = !(isPartialVerified && isRetryPage)
  const verifyButtonAnalytics =
    isPartialVerified && isRetryPage ? TrackingEvent.PRESSED_HARDID_RETRY_PAGE_VERIFY_BUTTON : TrackingEvent.PRESSED_HARDID_DRIVERS_LICENCE_PAGE_VERIFY_BUTTON

  const handleVerifyLaterClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_LATER_BUTTON)
    setShowVerifyLaterModal(true)
  }

  const handleModalClose = (): void => {
    setShowVerifyLaterModal(false)
  }

  const handleVerifyNowClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_VERIFY_NOW_BUTTON)
    setShowVerifyLaterModal(false)
  }

  const handleSkipClick = (): void => {
    amplitude.logEvent(TrackingEvent.PRESSED_SKIP_BUTTON)
    setShowVerifyLaterModal(false)
    router.push(Route.HOME)
  }

  const { schema, initialFormData } = useMemo(() => {
    const onNameBlur = (): void => {
      const fullName = joinStrings([formData?.givenNames ?? '', formData?.otherGivenNames ?? '', formData?.surname ?? ''])

      setSchemaUpdates((currSchema) => ({
        ...currSchema,
        showProfileNameAlert: isDifferentWithProfileNames(fullName, { givenNames, otherGivenNames, surname }),
      }))
    }
    const components = {
      profileCard: () => <HardIdProfileCard />,
      terms: () => <Terms />,
      footer: () => {
        return (
          <>
            <Grid>
              <Button.Ghost testNameSpace='verify-licence-verify-later' disabled={lock} onClick={handleVerifyLaterClick} padding='Fluid'>
                {t('verifyIdentity:verifyLater:buttonText')}
              </Button.Ghost>
            </Grid>
            <Grid>
              <Button.Primary loading={lock} type='submit' disabled={lock} padding='Fluid'>
                {t('verifyIdentity:licence:cta')}
              </Button.Primary>
            </Grid>
          </>
        )
      },
      dobAlert: () => (
        <Message
          kind='Success'
          iconName='Profile'
          heading={t('verifyIdentity:alert:profileUpdateHeading')}
          description={t('verifyIdentity:alert:profileUpdateDOB')}
        />
      ),
      nameAlert: () => (
        <Message
          kind='Success'
          iconName='Profile'
          heading={t('verifyIdentity:alert:profileUpdateHeading')}
          description={t('verifyIdentity:alert:profileUpdateName')}
        />
      ),
    }
    // @ts-expect-error: OPERATION BLEED STOPPER
    const isUserFormError = USER_VERIFICATION_ERRORS.includes(error)
    return getSchema(consumer, components, t, schemaUpdates, onNameBlur, isUserFormError, isProfileEditable)
  }, [error, consumer, t, schemaUpdates, isProfileEditable, lock])
  const [formData, setFormData] = useState(initialFormData)

  setHiddenFieldsByCountry(schema, countryCode)

  const onSubmit = async (driversLicenceData: DriversLicenceForm): Promise<void> => {
    setError(null)
    amplitude.logEvent(verifyButtonAnalytics)
    setLock(true)
    const { dlCardNumber, ...driverLicence } = driversLicenceData

    // NZ does not have a state of issuer however, IdentityLicenseRequest needs one
    // so default to consumer state
    const request: IdentityLicenseRequest = {
      channel: Channel.PORTAL,
      countryCode,
      // @ts-expect-error: OPERATION BLEED STOPPER
      state: initialState,
      dateOfBirth: consumer?.dateOfBirth,
      surname: consumer?.surname,
      givenNames: consumer?.givenNames,
      otherGivenNames: consumer?.otherGivenNames,
      ...driverLicence,
    }

    if (showCardNumber) {
      request.dlCardNumber = dlCardNumber
    }

    if (compareObjects(request, submittedRequest)) {
      setLock(false)
      setError(IdentityErrorCode.IdentityAlreadyRejected)
      return
    }

    try {
      const response = await identifyVerifyLicence(request).unwrap()
      if (response.status === IdentityDocumentVerificationStatus.VERIFIED) {
        // @ts-expect-error: OPERATION BLEED STOPPER
        onSuccess()
        setLock(false)
      } else if (response.status === IdentityDocumentVerificationStatus.REJECTED) {
        setLock(false)
        // @ts-expect-error: OPERATION BLEED STOPPER
        setSubmittedRequest(request)
        setError(IdentityErrorCode.IdentityCheckRejected)
        onFailure?.()
      }
    } catch (e) {
      if ([ErrorCode.MaxAttemptsReached, ErrorCode.MaxAttemptsReachedForType].includes(e?.data?.errorCode)) {
        router.push(`${Route.TERMINAL_ERROR}/identityVerificationFailedManual`)
        return
      }

      // Handle regular errors with logging
      const error = getIdentityVerifyErrorCode(e)
      amplitude.logEvent(TrackingEvent.IDENTITY_VERIFICATION_ERROR, {
        component: 'DriversLicense',
        errorType: e?.status ?? 'unknown',
        errorCode: e?.data?.errorCode ?? 'unknown',
        resolvedError: error ?? 'default',
      })

      setError(error ?? IdentityErrorCode.Default)
      setLock(false)
    }
  }

  const onError = (errors: FieldErrors<DriversLicenceForm>): void => {
    console.log('error', errors)
  }

  useEffect(() => {
    if (typeof error !== 'undefined') {
      window?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
    }
  }, [error])

  const onChange = (data: DriversLicenceForm): void => {
    setError(null)
    setFormData(data)
    // state might be undefined for places like NZ (formbuilder doesnt register hidden fields)
    // so default to consumer state
    const { state = initialState, dateOfBirth, licenceNumber, dlCardNumber } = data
    // @ts-expect-error: OPERATION BLEED STOPPER
    const { licenceNumOpts, licenceMaxLength } = getLicenceValidations(countryCode, state, t, countryStateIsShowing)
    // @ts-expect-error: OPERATION BLEED STOPPER
    const cardNumOpts = getLicenceCardNumberValidation(countryCode, state, t, countryStateIsShowing)
    const didStateUpdate = lastSelectedState.current !== state
    // licence num should not clear for countries with no state selection
    const shouldLicenceNumClear = didStateUpdate && countryStateIsShowing && !isLicenceNumValid(licenceNumber, licenceNumOpts) && licenceNumber.length > 0

    const shouldLicenceCardNumClear =
      // @ts-expect-error: OPERATION BLEED STOPPER
      didStateUpdate && countryStateIsShowing && showCardNumber && dlCardNumber?.length > 0 && !isLicenceNumValid(dlCardNumber, cardNumOpts)

    setSchemaUpdates((currSchema) => ({
      ...currSchema,
      showProfileDobAlert: isDifferentWithProfileDOB(dateOfBirth, profileDOB),
      licenceNumOpts,
      licenceMaxLength,
      cardNumOpts,
      showCardNumber,
      // using a random key to clear the licence num field
      ...(shouldLicenceNumClear && { licenceNumberKey: Math.random().toString(36) }),
      ...(shouldLicenceCardNumClear && { licenceCardNumberKey: Math.random().toString(36) }),
    }))

    lastSelectedState.current = state
  }

  return (
    <>
      <HardIdHeader documentType='licence' />
      <ErrorMessage identityType='licence' error={error} />
      <FormWrapper>
        <FormBuilder schema={schema} testNameSpace={'verify-licence'} onChange={onChange} onSubmit={onSubmit} onError={onError} disabled={lock} />
      </FormWrapper>
      <HardIdHelpLink />

      <VerifyLaterModal
        show={showVerifyLaterModal}
        onClose={handleModalClose}
        onVerifyNow={handleVerifyNowClick}
        onSkip={handleSkipClick}
        testNameSpace='verify-later'
      />
    </>
  )
}
